/**
 * The history rail's words (board P): what a conversation waits for, and when it last moved.
 *
 * `t`, the clock, the zone and the locale arrive as parameters; the boundaries between a clock time,
 * a weekday and a date are `entities/ai-conversation`'s `historyMoment`, the same ones the rail's
 * groups use.
 */

import type { TFunction } from 'i18next';

import { historyMoment, type AiHistoryEntry } from '~/entities/ai-conversation';
import { dateTimeFormat } from '~/shared/lib/intlCache';

/**
 * „Szkic gotowy · 2 wersje", „Zarys czeka na akceptację"…; `null` while it waits for nothing. A
 * conversation Buddy is working on says that first: what it waits for is about to change.
 */
export function waitsForWords(t: TFunction, entry: AiHistoryEntry): string | null {
  if (entry.working) return t('web:aiPlan.history.waits.working');
  switch (entry.waitsFor) {
    case 'answers':
      return t('web:aiPlan.history.waits.answers');
    case 'approval':
      return t('web:aiPlan.history.waits.approval');
    case 'draft':
      return entry.draftVersions > 1
        ? t('web:aiPlan.history.waits.draftVersions', { count: entry.draftVersions })
        : t('web:aiPlan.history.waits.draft');
    case 'saved':
      return t('web:aiPlan.history.waits.saved');
    case 'nothing':
      return null;
  }
}

/** „10:42" today, „wt." within the last week, „14.09" beyond it. */
export function momentWords(iso: string, now: Date, timeZone: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  switch (historyMoment(iso, now, timeZone)) {
    case 'time':
      return dateTimeFormat(locale, { timeZone, hour: '2-digit', minute: '2-digit' }).format(at);
    case 'weekday':
      return dateTimeFormat(locale, { timeZone, weekday: 'short' }).format(at);
    case 'date':
      return dateTimeFormat(locale, { timeZone, day: '2-digit', month: '2-digit' }).format(at);
  }
}
